import React from "react";
import type { Column, Row } from "@tanstack/react-table";
import Trow, { type TrowProps } from "../Trow/Trow";
import SkeletonLoader from "../../SkeletonLoader/SkeletonLoader";
import Loader from "../../Loader/Loader";
import { getTableRowDetailId } from "../tableRowDetailId";
import TableDetailErrorBoundary from "./TableDetailErrorBoundary";

const LOADER_DT = [300, 300, 300, 300, 300, 300];

// react-virtual (legacy, untyped) exposes virtual items with at least an index;
// only `index` is consumed here. TODO: replace with library type if react-virtual is upgraded.
interface VirtualRow {
  index: number;
}

interface StyleProps {
  paddingTop?: number;
  paddingBottom?: number;
}

export interface TbodyProps<TData = unknown> {
  rows: Row<TData>[];
  virtualRows: VirtualRow[];
  size?: "small" | "large";
  onRowClick?: (row: Row<TData>) => void;
  rowHeight?: number | string;
  noSearchResultsTitle?: string;
  noSearchResultsSubtitle?: string;
  infiniteScroll?: boolean;
  "data-testid"?: string;
  styleProps?: StyleProps;
  loadingMore?: boolean;
  selectedRowId?: string | number;
  enableTableViewEdit?: boolean;
  columns?: Column<unknown>[];
  lastPinnedColumn?: number;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactNode;
}

const Tbody = <TData,>({
  rows,
  virtualRows,
  size,
  onRowClick,
  rowHeight,
  noSearchResultsTitle,
  noSearchResultsSubtitle,
  infiniteScroll,
  "data-testid": dataTestId,
  styleProps,
  loadingMore,
  selectedRowId,
  enableTableViewEdit,
  columns,
  lastPinnedColumn,
  renderSubComponent,
}: TbodyProps<TData>) => {
  const getStaticRow = () => {
    return rows?.length > 0 ? (
      rows?.map((row) => {
        return getRow(row, getSubComponent(row));
      })
    ) : (
      <div className={"tr-no-results"}>
        <Loader
          wrapperHeight
          center
          title={noSearchResultsTitle}
          subtitle={noSearchResultsSubtitle}
        />
      </div>
    );
  };

  const getVirtualRows = () => {
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];
      return getRow(row);
    });
  };

  // Grouped rows expand into their subRows, never into a sub-component.
  const getSubComponent = (row: Row<TData>): React.ReactNode => {
    if (!renderSubComponent || !row.getIsExpanded() || row.subRows.length) {
      return null;
    }
    try {
      return renderSubComponent({ row });
    } catch {
      return null;
    }
  };

  // Detail rows are static-only: virtual rows assume a fixed row height.
  const getRow = (row: Row<TData>, detail?: React.ReactNode) => {
    const hasDetail =
      typeof detail !== "boolean" &&
      detail !== null &&
      detail !== undefined &&
      detail !== "";
    const detailId = hasDetail
      ? getTableRowDetailId(dataTestId, row.id)
      : undefined;
    return (
      <React.Fragment key={row.id}>
        <Trow
          // Trow is not generic; widen at this boundary only.
          row={row as Row<unknown>}
          onRowClick={onRowClick as TrowProps["onRowClick"]}
          data-testid={dataTestId}
          rowHeight={rowHeight}
          isSelected={
            !!selectedRowId &&
            selectedRowId === (row?.original as { id?: string | number })?.id
          }
          data-id={(row?.original as { id?: string | number })?.id}
          enableTableViewEdit={enableTableViewEdit}
          columns={columns}
          lastPinnedColumn={lastPinnedColumn}
        />
        {hasDetail && (
          <tr className={"tr-detail"} id={detailId} data-testid={detailId}>
            <td className={"td-detail"} colSpan={row.getVisibleCells().length}>
              <TableDetailErrorBoundary>{detail}</TableDetailErrorBoundary>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <tbody
      className={`tbody ${size === "small" ? "is-small" : "is-large"}`}
      data-testid={`${dataTestId}-body`}
    >
      {infiniteScroll ? (
        <>
          {styleProps?.paddingTop! > 0 && (
            <tr>
              <td style={{ height: `${styleProps?.paddingTop}px` }} />
            </tr>
          )}
          {getVirtualRows()}
          {loadingMore && (
            <tr className={`table-loader-row`}>
              {LOADER_DT.map((col, index) => {
                return (
                  <td
                    key={index}
                    className={`table-loader-td`}
                    style={{
                      width: `${col}px`,
                      height: `${rowHeight}px`,
                      minHeight: `${rowHeight}px`,
                    }}
                  >
                    <SkeletonLoader
                      height={"19px"}
                      width={"100%"}
                      data-testid={"table-loader"}
                    />
                  </td>
                );
              })}
            </tr>
          )}
          {styleProps?.paddingBottom! > 0 && (
            <tr>
              <td style={{ height: `${styleProps?.paddingBottom}px` }} />
            </tr>
          )}
        </>
      ) : (
        getStaticRow()
      )}
    </tbody>
  );
};

export default Tbody;
